import * as Sentry from "@sentry/vue";
import store from '@/store'

const IS_AI = import.meta.env.VITE_ENVIRONMENT_NAME == 'ai'
const PRESERVED_KEYS = ['id'];
const PRESERVED_KEY_SUFFIXES = ['_id'];

// The real DSN never ships to the browser. Envelopes go to the authenticated
// Rails tunnel, which rewrites this placeholder with the DSN from its ENV and
// forwards to Sentry ingest. Must match V1::SentryTunnelController::PLACEHOLDER_DSN.
export const PLACEHOLDER_DSN = 'https://placeholder@placeholder.invalid/0';

const isPreservedKey = (key) => {
  return PRESERVED_KEYS.includes(key) || PRESERVED_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
}

/**
 * Masks query params in a URL string to avoid leaking PHI/PII.
 * This can happen on listing pages, where selected table filters update URL
 * and filters can be patient name, mrn, etc.
 *
 * @param {string} urlString - The URL string to mask
 * @returns {string} The masked URL string
 */
const maskUrlQueryParams = (urlString) => {
  try {
    if (!urlString || typeof urlString !== 'string') {
      return urlString;
    }

    if (!urlString.startsWith('http')) {
      urlString = import.meta.env.VITE_BASE_URL + urlString
    }
    const url = new URL(urlString);

    if (!url.search) {
      return urlString;
    }

    const params = new URLSearchParams(url.search);
    for (const [key] of params.entries()) {
      if (!isPreservedKey(key)) {
        params.set(key, '***');
      }
    }
    url.search = params.toString();
    return url.toString();
  } catch {
    return urlString;
  }
};

// breadcrumbs include navigation, xhr, console logs, user clicks and many others
// we're looking only for xhr and navigation events
const processBreadcrumb = (breadcrumb) => {
  if (breadcrumb.category === 'xhr' && breadcrumb.data?.url) {
    breadcrumb.data.url = maskUrlQueryParams(breadcrumb.data.url);
  } else if (breadcrumb.category === 'navigation') {
    if (breadcrumb.data?.from) {
      breadcrumb.data.from = maskUrlQueryParams(breadcrumb.data.from);
    }
    if (breadcrumb.data?.to) {
      breadcrumb.data.to = maskUrlQueryParams(breadcrumb.data.to);
    }
  }
  return breadcrumb;
};

// Sends envelopes through the Rails tunnel with the user's credentials, read at
// send time because the Auth0 token rotates. Pre-auth (no token yet) there is
// no way to authenticate, so drop the event with a synthetic 204 — a real
// error response would put the SDK into a retry loop.
const tunnelFetch = (url, options) => {
  if (!store.state.token) {
    return Promise.resolve(new Response(null, { status: 204 }));
  }

  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${store.state.token}`,
      'Session-Id': store.state.uniqueSessionId
    }
  });
};

export const initSentry = (app) => {
  Sentry.init({
    app,
    dsn: PLACEHOLDER_DSN,
    tunnel: `${import.meta.env.VITE_API_BASE_URL}/v1/sentry/envelope`,
    transport: (options) => Sentry.makeFetchTransport(options, tunnelFetch),
    environment: import.meta.env.VITE_ENVIRONMENT_NAME,
    release: import.meta.env.VITE_SENTRY_RELEASE,

    beforeBreadcrumb(breadcrumb) {
      if (!IS_AI) return breadcrumb;

      return processBreadcrumb(breadcrumb);
    },

    beforeSend(event) {
      if (!IS_AI) return event;

      if (event?.request?.url) {
        event.request.url = maskUrlQueryParams(event.request.url);
      }
      if (event?.request?.headers?.Referer) {
        event.request.headers.Referer = maskUrlQueryParams(event.request.headers.Referer);
      }

      if (event?.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(processBreadcrumb);
      }

      return event;
    }
  });
};
